import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BookOpen, Check, ChevronRight, Clock, Star, Zap } from 'lucide-react';
import { cn, formatCurrency } from '@/lib/utils';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import DocumentHead from '@/components/DocumentHead';
import { IS_RUMIO } from '@/config/site';

// rumio_platform_plans row shape returned from /api/platform/site-builder-plans.
// Used only on the rumio.world tenant — other tenants render course pricing
// from /subscription-plans (the legacy ecommerce-subscription catalog).
interface PlatformPlanRow {
  slug: string;
  name: string;
  description: string | null;
  tier_level: number;
  monthly_price_cents: number;
  annual_price_cents: number;
  features: Record<string, unknown>;
}

// Translate the JSONB features object the rumio_platform_plans row carries
// into a human-readable bullet list. Conservative — only known keys are
// surfaced so future features added to the JSONB don't leak as raw text.
//
// Keys match migration 203. `max_sites` and `byo_domain` are deliberately
// absent: pricing is per site (every plan is one), and custom domains ship on
// every plan gated on payment rather than tier.
const GATED_FEATURE_LABELS: Record<string, string> = {
  addie: 'Addie — AI advertising',
  dippy: 'Dippy — autonomous sourcing',
  multi_channel: 'Multi-channel (Amazon, Google, TikTok)',
  api_access: 'API access + webhooks',
  byok: 'Bring your own AI key',
  video: 'AI video generation',
  meilisearch: 'Meilisearch + analytics',
  volume_discounts: 'Volume-discount rules',
};

const SUPPORT_LABELS: Record<string, string> = {
  standard: 'AI assistant + tickets',
  priority: 'Priority support queue',
  priority_onboarding: 'Priority queue + onboarding session',
};

function deriveFeatureList(plan: PlatformPlanRow): string[] {
  const f = plan.features ?? {};
  const out: string[] = ['1 site', '0% platform transaction fees', 'Your own custom domain'];
  if (typeof f.ai_credits_monthly === 'number') {
    out.push(`${f.ai_credits_monthly.toLocaleString()} AI credits / month`);
  }
  if (typeof f.max_products === 'number') {
    out.push(`Up to ${f.max_products.toLocaleString()} products`);
  }
  if (typeof f.max_storage_gb === 'number') out.push(`${f.max_storage_gb} GB storage`);
  if (typeof f.max_staff === 'number') {
    out.push(f.max_staff === 1 ? '1 staff account' : `${f.max_staff} staff accounts`);
  }
  if (Array.isArray(f.features)) {
    for (const key of f.features as string[]) {
      const label = GATED_FEATURE_LABELS[key];
      if (label) out.push(label);
    }
  }
  const support = SUPPORT_LABELS[String(f.support ?? '')];
  if (support) out.push(support);
  return out;
}

export default function Pricing() {
  const { t } = useTranslation('pricing');

  const FAQ_KEYS = ['tryBeforeBuy', 'afterEnroll', 'expire', 'founders', 'refund', 'certificate'] as const;

  const faqItems = useMemo(
    () =>
      FAQ_KEYS.map((key) => ({
        question: t(`faq.items.${key}.question`),
        answer: t(`faq.items.${key}.answer`),
      })),
    [t],
  );

  function levelLabel(level: string): string {
    const key = `courses.levels.${level}` as const;
    const translated = t(key);
    if (translated === key) {
      return level.charAt(0).toUpperCase() + level.slice(1);
    }
    return translated;
  }

  const { data: coursesData, isLoading: coursesLoading } = useQuery({
    queryKey: ['courses-pricing'],
    queryFn: () =>
      api.get<{ courses: any[]; total: number }>('/courses?limit=50'),
  });

  // On rumio.world: fetch platform hosting plans (starter/growth/pro) from
  // rumio_platform_plans. On every other tenant: fetch the tenant's own
  // subscription-plan catalog (course/ecom subs).
  const { data: plansData, isLoading: plansLoading } = useQuery({
    queryKey: ['pricing-plans', IS_RUMIO ? 'platform' : 'tenant'],
    queryFn: () =>
      IS_RUMIO
        ? api
            .get<{ plans: PlatformPlanRow[] }>('/platform/site-builder-plans')
            .catch(() => ({ plans: [] as PlatformPlanRow[] }))
            .then((r) => ({
              plans: r.plans.map((p) => ({
                id: p.slug,
                name: p.name,
                description: p.description,
                price: (p.monthly_price_cents / 100).toString(),
                interval: 'month',
                features: deriveFeatureList(p),
                is_popular: p.slug === 'growth',
                ctaHref: `/signup?plan=${encodeURIComponent(p.slug)}`,
              })),
            }))
        : api.get<{ plans: any[] }>('/subscription-plans').catch(() => ({ plans: [] })),
  });

  const courses = coursesData?.courses ?? [];
  const plans = plansData?.plans ?? [];
  const isLoading = coursesLoading || plansLoading;

  const coursesWithFounders = courses.filter(
    (c: any) =>
      c.founders_pricing_id && c.slots_used < c.max_slots
  );

  return (
    <div className="bg-[--bg-warm] min-h-screen">
      <DocumentHead
        title={t('documentHead.title')}
        description={t('documentHead.description')}
        canonicalUrl="/pricing"
      />

      {/* Hero */}
      <div className="bg-cta text-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-display text-4xl font-bold">{t('hero.title')}</h1>
          <p className="mt-3 text-lg text-white/80 max-w-2xl mx-auto">
            {t('hero.subtitle')}
          </p>
        </div>
      </div>

      <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        {/* Breadcrumbs */}
        <nav aria-label={t('breadcrumbs.label')} className="mb-8">
          <ol className="flex items-center gap-1.5 text-sm text-ink-muted">
            <li>
              <Link to="/" className="hover:text-ink-soft transition-colors">
                {t('breadcrumbs.home')}
              </Link>
            </li>
            <li aria-hidden="true">
              <ChevronRight className="h-4 w-4" />
            </li>
            <li>
              <span className="font-medium text-ink" aria-current="page">
                {t('breadcrumbs.current')}
              </span>
            </li>
          </ol>
        </nav>

        {/* Subscription Plans */}
        {plans.length > 0 && (
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-ink text-center mb-8">
              {t('subscriptionPlans')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {plans.map((plan: any) => {
                const features = plan.features || [];
                const isPopular = plan.is_popular || plan.metadata?.popular;

                return (
                  <div
                    key={plan.id}
                    className={cn(
                      'relative rounded-xl border bg-surface shadow-sm overflow-hidden flex flex-col',
                      isPopular
                        ? 'border-[var(--color-primary-400)] ring-2 ring-[var(--color-primary-200)]'
                        : 'border-line'
                    )}
                  >
                    {isPopular && (
                      <div className="bg-cta text-white text-center text-xs font-semibold py-1.5 uppercase tracking-wider">
                        {t('mostPopular')}
                      </div>
                    )}
                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="text-xl font-bold text-ink">
                        {plan.name}
                      </h3>
                      {plan.description && (
                        <p className="mt-1 text-sm text-ink-muted">
                          {plan.description}
                        </p>
                      )}

                      <div className="mt-4 flex items-baseline gap-1">
                        <span className="text-4xl font-bold text-[var(--color-primary-700)]">
                          {formatCurrency(Number(plan.price))}
                        </span>
                        {plan.interval && (
                          <span className="text-ink-muted">
                            {t('perInterval', { interval: plan.interval })}
                          </span>
                        )}
                      </div>

                      {features.length > 0 && (
                        <ul className="mt-6 space-y-3 flex-1">
                          {features.map((feature: string, i: number) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-ink-soft">
                              <Check className="h-4 w-4 text-sage-600 shrink-0 mt-0.5" aria-hidden="true" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      )}

                      <div className="mt-6">
                        {plan.ctaHref ? (
                          <Link to={plan.ctaHref}>
                            <Button
                              variant={isPopular ? 'primary' : 'boutique-outline'}
                              size="lg"
                              className="w-full"
                            >
                              {t('getStarted')}
                            </Button>
                          </Link>
                        ) : (
                          <Button
                            variant={isPopular ? 'primary' : 'boutique-outline'}
                            size="lg"
                            className="w-full"
                          >
                            {t('getStarted')}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Founders Pricing Callout */}
        {coursesWithFounders.length > 0 && (
          <section className="mb-16">
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-6 lg:p-8 text-center">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Star className="h-5 w-5 text-amber-500" aria-hidden="true" />
                <h2 className="text-xl font-bold text-amber-900">
                  {t('founders.heading')}
                </h2>
                <Star className="h-5 w-5 text-amber-500" aria-hidden="true" />
              </div>
              <p className="text-amber-800 max-w-2xl mx-auto mb-6">
                {t('founders.blurb')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
                {coursesWithFounders.map((course: any) => {
                  const slotsRemaining = course.max_slots - course.slots_used;
                  return (
                    <Link
                      key={course.id}
                      to={`/courses/${course.slug}`}
                      className="group rounded-lg border border-amber-200 bg-surface p-4 text-left hover:shadow-md transition-shadow"
                    >
                      <h3 className="font-semibold text-ink group-hover:text-[var(--color-primary-700)] transition-colors">
                        {course.name}
                      </h3>
                      <div className="mt-2 flex items-baseline gap-2">
                        <span className="text-lg font-bold text-[var(--color-primary-700)]">
                          {formatCurrency(Number(course.founders_price))}
                        </span>
                        <span className="text-sm text-ink-muted line-through">
                          {formatCurrency(Number(course.price))}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-amber-700 font-medium">
                        {t('founders.spotsRemaining', { count: slotsRemaining })}
                      </p>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Individual Course Pricing */}
        <section className="mb-16">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-ink">
              {plans.length > 0 ? t('courses.headingWithPlans') : t('courses.heading')}
            </h2>
            <p className="mt-2 text-ink-soft">
              {t('courses.subtitle')}
            </p>
          </div>

          {isLoading ? (
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              aria-busy="true"
              aria-label={t('courses.loadingAriaLabel')}
            >
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse rounded-xl border border-line bg-surface p-6" aria-hidden="true">
                  <div className="h-5 bg-surface-muted rounded w-3/4 mb-3" />
                  <div className="h-4 bg-surface-muted rounded w-1/2 mb-4" />
                  <div className="h-8 bg-surface-muted rounded w-1/3 mb-4" />
                  <div className="h-10 bg-surface-muted rounded w-full" />
                </div>
              ))}
            </div>
          ) : courses.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="mx-auto h-12 w-12 text-ink-muted" aria-hidden="true" />
              <p className="mt-4 text-ink-muted">
                {t('courses.empty')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((course: any) => {
                const hasFounders =
                  course.founders_pricing_id &&
                  course.slots_used < course.max_slots;
                const price = hasFounders
                  ? Number(course.founders_price)
                  : Number(course.price);
                const originalPrice = Number(course.price);
                const isFree = course.price_type === 'free';

                return (
                  <div
                    key={course.id}
                    className="rounded-xl border border-line bg-surface shadow-sm overflow-hidden flex flex-col"
                  >
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {course.level && (
                          <Badge variant={
                            course.level === 'beginner' ? 'green' :
                            course.level === 'intermediate' ? 'yellow' : 'red'
                          }>
                            {levelLabel(course.level)}
                          </Badge>
                        )}
                        {hasFounders && (
                          <Badge variant="blue">{t('founders.badge')}</Badge>
                        )}
                      </div>

                      <h3 className="text-lg font-bold text-ink">
                        {course.name}
                      </h3>

                      {course.short_description && (
                        <p className="mt-1 text-sm text-ink-muted line-clamp-2">
                          {course.short_description}
                        </p>
                      )}

                      <div className="mt-auto pt-4">
                        {/* Meta */}
                        <div className="flex items-center gap-3 text-xs text-ink-muted mb-3">
                          {course.lesson_count != null && (
                            <span className="flex items-center gap-1">
                              <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
                              {t('courses.lessonsCount', { count: course.lesson_count })}
                            </span>
                          )}
                          {course.total_duration != null && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                              {t('courses.durationHours', { hours: Math.round(course.total_duration / 60) })}
                            </span>
                          )}
                        </div>

                        {/* Price */}
                        <div className="flex items-baseline gap-2 mb-4">
                          {isFree ? (
                            <span className="text-xl font-bold text-sage-700">
                              {t('courses.free')}
                            </span>
                          ) : (
                            <>
                              <span className="text-2xl font-bold text-[var(--color-primary-700)]">
                                {formatCurrency(price)}
                              </span>
                              {hasFounders && price < originalPrice && (
                                <span className="text-sm text-ink-muted line-through">
                                  {formatCurrency(originalPrice)}
                                </span>
                              )}
                            </>
                          )}
                        </div>

                        <Link to={`/courses/${course.slug}`}>
                          <Button
                            variant="boutique-outline"
                            size="md"
                            className="w-full"
                          >
                            {t('courses.viewCourse')}
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* FAQ */}
        <section className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-ink text-center mb-8">
            {t('faq.heading')}
          </h2>
          <div className="space-y-4">
            {faqItems.map((item, i) => (
              <details
                key={i}
                className="group rounded-xl border border-line bg-surface overflow-hidden"
              >
                <summary className="flex items-center justify-between cursor-pointer px-6 py-4 text-left font-semibold text-ink hover:bg-surface-soft transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-primary-600)]">
                  {item.question}
                  <ChevronRight className="h-5 w-5 text-ink-muted shrink-0 ml-2 transition-transform group-open:rotate-90" aria-hidden="true" />
                </summary>
                <div className="px-6 pb-4 text-ink-soft text-sm leading-relaxed">
                  {item.answer}
                </div>
              </details>
            ))}
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="mt-16 text-center">
          <div className="rounded-xl bg-[var(--color-primary-50)] border border-[var(--color-primary-200)] p-8 lg:p-12">
            <Zap className="mx-auto h-8 w-8 text-[var(--color-primary-600)] mb-4" aria-hidden="true" />
            <h2 className="text-2xl font-bold text-ink">
              {t('bottomCta.heading')}
            </h2>
            <p className="mt-2 text-ink-soft max-w-lg mx-auto">
              {t('bottomCta.subtitle')}
            </p>
            <Link to="/courses" className="inline-block mt-6">
              <Button variant="primary" size="lg">
                {t('bottomCta.browseAll')}
              </Button>
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
